import React, { FC, useContext } from 'react';
import get from 'lodash/get';

import InputField, { InputType } from 'src/presentation/components/forms/InputField';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import NodeSelector from '../NodeSelector';
import { Content, Footer, Row } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import {
  INPUT_TYPES_OPTIONS,
  NodeProps,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { NanoSecondsInputField } from 'src/presentation/components/pages/shared/NanoSecondsInputField';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const KeypadInputNode: FC<NodeProps> = ({ attr, onSelectNode, nodeList, readOnly, disabled }) => {
  const { workingState } = useContext(FormContext);
  const currentNodeId = get(workingState, `${attr}.id`);
  const inputType = get(workingState, `${attr}.type`);
  const isDtmfInputType = inputType === 'DTMF' || inputType === 'DTMF_SPEECH';

  const isFeatureEnabled = useIsFeatureEnabled();
  const isTwilioGatherV2Enabled = isFeatureEnabled('twilioGatherV2');

  // if ff `twilioGatherV2` enabled timeout input should be visible for and inputType
  const isTimeoutInputVisible = isTwilioGatherV2Enabled || inputType === 'SPEECH' || inputType === 'DTMF_SPEECH';

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid={`${attr}-name`}
            description="The name you can refer to link to other nodes"
            disabled={disabled}
            label="Name"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid={`${attr}-description`}
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <Dropdown
            attr={`${attr}.type`}
            data-aid={`${attr}.type`}
            description="DTMF is through a keypad. SPEECH is through customer’s speech input and DTMF/SPEECH is either"
            disabled={disabled}
            label="Input Type"
            options={INPUT_TYPES_OPTIONS}
            readOnly={readOnly}
          />
        </Row>

        {isDtmfInputType && (
          <Row>
            <InputField
              attr={`${attr}.numDigits`}
              data-aid={`${attr}.numDigits`}
              description="The expected number of digits your customer can input into the keypad"
              disabled={disabled}
              label="Number of Digits"
              readOnly={readOnly}
              type={InputType.number}
            />
          </Row>
        )}

        {isTimeoutInputVisible && (
          <>
            <Row>
              <NanoSecondsInputField
                attr={`${attr}.timeout`}
                data-aid={`${attr}.timeout`}
                description="Time in seconds to wait for the caller to press another digit or say another word before considering the input to be complete"
                disabled={disabled}
                label={isTwilioGatherV2Enabled ? 'Timeout' : 'Speech Timeout'}
                readOnly={readOnly}
              />
            </Row>
            <Row>
              <InputField
                attr={`${attr}.hints`}
                data-aid={`${attr}.hints`}
                description="Expected answers customers might say. This will help used for increased speech recognition accuracy (optional)"
                disabled={disabled}
                label="Hints"
                readOnly={readOnly}
              />
            </Row>
          </>
        )}
      </Content>

      <Footer>
        <Row>
          <NodeSelector
            searchable
            attr={`${attr}.nextId`}
            data-aid={`${attr}.nextId`}
            description="The next node in the IVR"
            disabled={disabled}
            label="Next Node"
            options={nodeList}
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            readOnly={readOnly}
          />
        </Row>
      </Footer>
    </>
  );
};

export default KeypadInputNode;
